import React, { useState } from 'react';

import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

// Components
import { Mui, SwipeableViews } from 'components';
import { DisplayData } from 'components/common';

const useDotsStyles = Mui.makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
  },
  dot: {
    backgroundColor: theme.palette.action.disabled,
    borderRadius: '50%',
    width: 8,
    height: 8,
    margin: theme.spacing(0, 0.5),
    padding: 0,
    border: 'none',
    cursor: 'pointer',
  },
  dotActive: {
    backgroundColor: theme.palette.primary.main,
  },
}));

function Dots(props) {
  const { items, activeStep, onSelect } = props;
  const classes = useDotsStyles();

  return (
    <Mui.Box className={classes.root}>
      {items.map((item, index) => (
        <Mui.Box
          key={`${item?.email}-${item?.name}`}
          component="button"
          type="button"
          aria-label={`Ver ${item?.name || 'gestor'}`}
          onClick={() => onSelect(index)}
          className={clsx(classes.dot, index === activeStep && classes.dotActive)}
        />
      ))}
    </Mui.Box>
  );
}

Dots.propTypes = {
  items: PropTypes.array,
  activeStep: PropTypes.number,
  onSelect: PropTypes.func,
};

const useCardStyles = Mui.makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2.5),
    userSelect: 'none',
    '& img': {
      WebkitUserDrag: 'none',
    },
  },
  avatarWrapper: {
    '& .MuiAvatar-root': {
      width: 64,
      height: 64,
    },
  },
  copyable: {
    cursor: 'pointer',
    fontSize: '0.95rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gridGap: theme.spacing(0.5),
  },
  iconButton: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  nameResponsive: {
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  userInfo: {
    flexGrow: 1,
    minWidth: 0,
  },
}));

function CustomerSuccessCard(props) {
  const { customerSuccess } = props;

  // Hooks
  const { enqueueSnackbar } = useSnackbar();
  const classes = useCardStyles();

  // Constants
  const { countryCode, areaCode, phoneNumber } = customerSuccess?.phoneNumber || {};
  const hasPhone = !!customerSuccess?.phoneNumber?.uid;
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${[countryCode, areaCode, phoneNumber]
    .filter(Boolean)
    .join('')}`;

  // Actions
  const handleCopy = (value, label) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(() =>
        enqueueSnackbar(`${label} copiado com sucesso`, {
          variant: 'info',
          autoHideDuration: 2000,
        }),
      );
    }
  };

  return (
    <Mui.Paper variant="outlined" className={classes.paper}>
      <Mui.Box display="flex" alignItems="center" gridGap={16} className={classes.avatarWrapper}>
        <Mui.Box className={classes.userInfo}>
          <DisplayData.User
            disableCaption
            avatarBordered
            fullName={customerSuccess?.name}
            picture={customerSuccess?.picture}
            typographyProps={{
              className: classes.nameResponsive,
              style: { fontSize: '1.1rem', fontWeight: 700 },
            }}>
            {!!customerSuccess?.email && (
              <Mui.Typography
                variant="body1"
                component="p"
                color="primary"
                noWrap
                className={classes.copyable}
                onClick={() => handleCopy(customerSuccess.email, 'E-mail')}>
                {customerSuccess.email}
              </Mui.Typography>
            )}
            {hasPhone && (
              <DisplayData.PhoneNumberField
                countryCode={countryCode}
                areaCode={areaCode}
                number={phoneNumber}
                color="primary"
                className={classes.copyable}
                onClick={() =>
                  handleCopy(
                    [countryCode, areaCode, phoneNumber].filter(Boolean).join(' '),
                    'Telefone',
                  )
                }
              />
            )}
          </DisplayData.User>
        </Mui.Box>
        <Mui.Box className={classes.actions}>
          {!!customerSuccess?.email && (
            <Mui.Tooltip title="Enviar e-mail">
              <Mui.IconButton
                size="small"
                className={classes.iconButton}
                component="a"
                href={`mailto:${customerSuccess.email}`}
                target="_blank"
                rel="noopener noreferrer">
                <Mui.Icon fontSize="default">mail</Mui.Icon>
              </Mui.IconButton>
            </Mui.Tooltip>
          )}
          {hasPhone && (
            <Mui.Tooltip title="Conversar no WhatsApp">
              <Mui.IconButton
                size="small"
                className={classes.iconButton}
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer">
                <Mui.Icon fontSize="default">phone</Mui.Icon>
              </Mui.IconButton>
            </Mui.Tooltip>
          )}
        </Mui.Box>
      </Mui.Box>
    </Mui.Paper>
  );
}

CustomerSuccessCard.propTypes = {
  customerSuccess: PropTypes.object,
};

function CustomerSuccessSection(props) {
  const { customerSuccesses } = props;

  // States
  const [activeStep, setActiveStep] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  if (!Array.isArray(customerSuccesses) || customerSuccesses.length === 0) return null;

  if (customerSuccesses.length === 1) {
    return <CustomerSuccessCard customerSuccess={customerSuccesses[0]} />;
  }

  return (
    <Mui.Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <SwipeableViews
        autoplay={!isHovered}
        interval={5000}
        enableMouseEvents
        hysteresis={0.2}
        style={{ overflowY: 'visible', touchAction: 'pan-y' }}
        containerStyle={{ willChange: 'auto' }}
        slideStyle={{ overflow: 'visible' }}
        index={activeStep}
        onChangeIndex={setActiveStep}>
        {customerSuccesses.map(customerSuccess => (
          <Mui.Box key={customerSuccess?.email} px={0.5}>
            <CustomerSuccessCard customerSuccess={customerSuccess} />
          </Mui.Box>
        ))}
      </SwipeableViews>
      <Dots items={customerSuccesses} activeStep={activeStep} onSelect={setActiveStep} />
    </Mui.Box>
  );
}

CustomerSuccessSection.propTypes = {
  customerSuccesses: PropTypes.array,
};

export default CustomerSuccessSection;
